/**
 * styles.css — The Wine Engine layout only (no hard-coded theme colors).
 *
 * Colors come from the live tenant theme via applyTheme() / --ws-* and
 * --ws-rich-* CSS variables. Do not override primary/accent/header here.
 *
 * Scoped under both folder slug and live BE tenant_id:
 *   wineengine | thewineengine
 */

:host(.ws-tenant-wineengine) .ws-prompt-chip:hover,
:host(.ws-tenant-thewineengine) .ws-prompt-chip:hover {
  background: var(--ws-selected-chip, var(--ws-primary));
  border-color: var(--ws-selected-chip, var(--ws-primary));
  color: #ffffff;
}

/* Promo strip — old .promotion-text / .promo-text-right (ss1 white pill CTA).
   Caption + CTA each wrap to exactly 2 lines (\\n from theme + pre-line). */
:host(.ws-tenant-wineengine) .ws-banner-promo,
:host(.ws-tenant-thewineengine) .ws-banner-promo {
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  font-weight: 600;
  background-color: var(--ws-accent, #9c5564);
  /* Drop Fogarty gold strip when no promotion.png is configured. */
  background-image: none;
}

:host(.ws-tenant-wineengine) .ws-banner-promo--image,
:host(.ws-tenant-thewineengine) .ws-banner-promo--image {
  /* Image is the mauve promotion strip; drop shared gold/dot fallback layers. */
  background-color: transparent;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

:host(.ws-tenant-wineengine) .ws-banner-text,
:host(.ws-tenant-thewineengine) .ws-banner-text {
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
  max-width: none;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.45;
}

/* Each <span>…</span><br><span>… is one visual line — never wrap further. */
:host(.ws-tenant-wineengine) .ws-banner-text > span,
:host(.ws-tenant-thewineengine) .ws-banner-text > span {
  display: block;
  white-space: nowrap;
}

:host(.ws-tenant-wineengine) .ws-banner-cta,
:host(.ws-tenant-thewineengine) .ws-banner-cta {
  background: #ffffff;
  color: #9c5564;
  border: none;
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 1.1;
  text-transform: uppercase;
  text-align: center;
  max-width: none;
  flex: 0 0 auto;
  display: inline-block;
}

:host(.ws-tenant-wineengine) .ws-banner-cta:hover,
:host(.ws-tenant-thewineengine) .ws-banner-cta:hover {
  background: #f2f2f2;
  opacity: 1;
}

/* ── Wine listing card layout (old .carousel .card structure) ─────────── */
:host(.ws-tenant-wineengine)
  .ws-carousel-track
  > .ws-carousel-item:has(.ws-we-card),
:host(.ws-tenant-thewineengine)
  .ws-carousel-track
  > .ws-carousel-item:has(.ws-we-card) {
  width: 240px;
}

:host(.ws-tenant-wineengine) .ws-we-card,
:host(.ws-tenant-thewineengine) .ws-we-card {
  position: relative;
  box-sizing: border-box;
  height: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 12px;
  padding: 0.5rem;
  background: var(--ws-background, #fff);
  border-radius: 12px;
  border: none;
  box-shadow: 0 0 10px 0 rgba(189, 133, 39, 0.4);
  font-family: var(--ws-font);
  overflow: hidden;
}

:host(.ws-tenant-wineengine) .ws-we-discount,
:host(.ws-tenant-thewineengine) .ws-we-discount {
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
}

:host(.ws-tenant-wineengine) .ws-we-discount-pct,
:host(.ws-tenant-thewineengine) .ws-we-discount-pct {
  display: inline-block;
  width: 47px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  background: var(--ws-rich-cta-bg, var(--ws-primary));
  color: var(--ws-rich-on-accent, #fff);
  padding: 1px 3px;
  border-radius: 8px 0 6px 0;
}

:host(.ws-tenant-wineengine) .ws-we-soldout,
:host(.ws-tenant-thewineengine) .ws-we-soldout {
  position: absolute;
  right: 8px;
  top: 8px;
  text-align: center;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 3px;
  width: fit-content;
  background-color: #000000ab;
  color: #fff;
}

:host(.ws-tenant-wineengine) .ws-we-product-head,
:host(.ws-tenant-thewineengine) .ws-we-product-head {
  position: relative;
  height: 180px;
  min-height: 180px;
  max-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

:host(.ws-tenant-wineengine) .ws-we-product-head img,
:host(.ws-tenant-thewineengine) .ws-we-product-head img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
}

:host(.ws-tenant-wineengine) .ws-we-product-body,
:host(.ws-tenant-thewineengine) .ws-we-product-body {
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

:host(.ws-tenant-wineengine) .ws-we-tasting,
:host(.ws-tenant-thewineengine) .ws-we-tasting {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  align-self: center;
  width: max-content;
  max-width: 100%;
  margin: 0 auto 10px;
  padding: 8px 12px;
  border: 1px solid
    var(--ws-rich-tasting-border, var(--ws-rich-deep, var(--ws-primary)));
  border-radius: 20px;
  background: var(--ws-background, #fff);
  box-shadow: 0 4px 9.7px 0 rgba(0, 0, 0, 0.12);
  color: var(--ws-rich-accent, var(--ws-primary));
  font-size: 15px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  font-family: var(--ws-font);
  flex-shrink: 0;
  white-space: nowrap;
  box-sizing: border-box;
}

:host(.ws-tenant-wineengine) .ws-we-tasting svg,
:host(.ws-tenant-thewineengine) .ws-we-tasting svg {
  width: 12px;
  height: 15px;
  flex-shrink: 0;
  display: block;
  stroke: currentColor;
}

:host(.ws-tenant-wineengine) .ws-we-title,
:host(.ws-tenant-thewineengine) .ws-we-title {
  font-size: 15px;
  font-weight: 500;
  color: var(--ws-rich-title, var(--ws-text, #000));
  line-height: 1.25;
  margin: 0 0 4px !important;
  min-height: 38px;
  max-height: 38px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

:host(.ws-tenant-wineengine) .ws-we-meta,
:host(.ws-tenant-thewineengine) .ws-we-meta {
  margin: 0;
  font-size: 13px;
  font-weight: 400;
  color: var(--ws-text, #333);
  line-height: 1.3;
  min-height: 2.6em;
  max-height: 2.6em;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

:host(.ws-tenant-wineengine) .ws-we-product-details,
:host(.ws-tenant-thewineengine) .ws-we-product-details {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  width: 100%;
  margin-top: auto;
  padding-top: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

:host(.ws-tenant-wineengine) .ws-we-purchase-row,
:host(.ws-tenant-thewineengine) .ws-we-purchase-row {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  width: 100%;
}

:host(.ws-tenant-wineengine) .ws-we-product-footer,
:host(.ws-tenant-thewineengine) .ws-we-product-footer {
  text-align: left;
  width: 100%;
  flex: 1 1 auto;
  min-width: 0;
}

:host(.ws-tenant-wineengine) .ws-we-product-footer p,
:host(.ws-tenant-thewineengine) .ws-we-product-footer p {
  margin: 0;
  line-height: 1.1;
}

:host(.ws-tenant-wineengine) .ws-we-product-footer p:first-child,
:host(.ws-tenant-thewineengine) .ws-we-product-footer p:first-child {
  font-size: 1rem;
  color: var(--ws-rich-cta-bg, var(--ws-primary));
}

:host(.ws-tenant-wineengine) .ws-we-product-footer p:nth-child(2),
:host(.ws-tenant-wineengine) .ws-we-product-footer p:nth-child(3),
:host(.ws-tenant-thewineengine) .ws-we-product-footer p:nth-child(2),
:host(.ws-tenant-thewineengine) .ws-we-product-footer p:nth-child(3) {
  font-size: 0.75rem;
  color: var(--ws-text, #000);
  margin-block: 0;
  font-weight: 400;
}

/* "Add to Basket" — <button>, colors from tenant theme */
:host(.ws-tenant-wineengine) .ws-we-add,
:host(.ws-tenant-thewineengine) .ws-we-add,
:host(.ws-tenant-thewineengine) .ws-card-cta {
  appearance: none;
  -webkit-appearance: none;
  margin-left: auto;
  white-space: nowrap;
  padding: 4px 12px;
  min-height: 28px;
  background-color: var(--ws-rich-cta-bg, var(--ws-primary));
  color: var(--ws-rich-on-accent, #fff) !important;
  border: 1px solid transparent;
  border-radius: 0.2px;
  cursor: pointer;
  text-decoration: none;
  font-size: 14px;
  line-height: 2;
  font-family: var(--ws-font) !important;
  font-weight: 400 !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

:host(.ws-tenant-wineengine) .ws-we-add:hover,
:host(.ws-tenant-thewineengine) .ws-we-add:hover {
  background-color: var(--ws-rich-cta-bg-hover, var(--ws-primary));
}

:host(.ws-tenant-wineengine) .ws-we-action-mount,
:host(.ws-tenant-thewineengine) .ws-we-action-mount {
  margin-left: auto;
  flex: 0 0 auto;
}

/* Qty controls — old .product-action parity (ss2) */
:host(.ws-tenant-wineengine) .ws-we-product-action,
:host(.ws-tenant-wineengine) .ws-we-card .product-action,
:host(.ws-tenant-thewineengine) .ws-we-product-action,
:host(.ws-tenant-thewineengine) .ws-we-card .product-action {
  display: flex;
  align-items: stretch;
  gap: 10px;
  margin-left: auto;
  white-space: nowrap;
  width: auto;
}

:host(.ws-tenant-wineengine) .ws-we-qty-wrap,
:host(.ws-tenant-wineengine) .ws-we-card .product-quantity-wrapper,
:host(.ws-tenant-thewineengine) .ws-we-qty-wrap,
:host(.ws-tenant-thewineengine) .ws-we-card .product-quantity-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 0;
}

:host(.ws-tenant-wineengine) .ws-we-card .product-action button,
:host(.ws-tenant-wineengine) .ws-we-delete,
:host(.ws-tenant-wineengine) .ws-we-dec,
:host(.ws-tenant-wineengine) .ws-we-inc,
:host(.ws-tenant-thewineengine) .ws-we-card .product-action button,
:host(.ws-tenant-thewineengine) .ws-we-delete,
:host(.ws-tenant-thewineengine) .ws-we-dec,
:host(.ws-tenant-thewineengine) .ws-we-inc {
  appearance: none;
  -webkit-appearance: none;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  min-width: 30px;
  max-width: 30px;
  height: 30px;
  min-height: 30px;
  padding: 0 !important;
  border-radius: 2px;
  border: 1px solid #e5e5e5;
  background: #f1f1f1;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  color: #0a0a0a;
  font-size: 16px;
  font-weight: 500;
  line-height: 1;
  text-transform: none;
}

:host(.ws-tenant-wineengine) .ws-we-delete svg,
:host(.ws-tenant-wineengine) .ws-we-card .delete-cart svg,
:host(.ws-tenant-thewineengine) .ws-we-delete svg,
:host(.ws-tenant-thewineengine) .ws-we-card .delete-cart svg {
  width: 16px;
  height: 16px;
  display: block;
}

:host(.ws-tenant-wineengine) .ws-we-cart-qty,
:host(.ws-tenant-wineengine) .ws-we-card .cart-qty,
:host(.ws-tenant-thewineengine) .ws-we-cart-qty,
:host(.ws-tenant-thewineengine) .ws-we-card .cart-qty {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 30px;
  padding: 2px 10px;
  margin: 0;
  border: none;
  background: transparent;
  box-shadow: none;
  color: #0a0a0a;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  text-align: center;
}

:host(.ws-tenant-wineengine) .ws-we-view-basket,
:host(.ws-tenant-thewineengine) .ws-we-view-basket {
  border-color: var(--ws-primary);
  color: var(--ws-primary);
  flex: 0 0 auto;
  width: max-content;
}

:host(.ws-tenant-wineengine) .ws-we-view-basket:hover,
:host(.ws-tenant-thewineengine) .ws-we-view-basket:hover {
  background: color-mix(in srgb, var(--ws-primary) 8%, transparent);
}

/* Align View Basket with guided chips (Shop Our Wines, etc.) — same indent
   as .ws-prompts (avatar width + gap). Base CartActions sets 34px inline;
   Wine Engine override omitted it, so the button sat flush left (ss2). */
:host(.ws-tenant-wineengine) .ws-we-cart-actions,
:host(.ws-tenant-thewineengine) .ws-we-cart-actions {
  padding-left: calc(var(--ws-avatar-size) + var(--ws-avatar-gap));
  box-sizing: border-box;
}

/* ── Wine Quiz (old #chatbot-window quiz-* parity) ───────────────────── */
:host(.ws-tenant-wineengine) .ws-panel,
:host(.ws-tenant-thewineengine) .ws-panel {
  position: relative;
}

:host(.ws-tenant-wineengine) .ws-quiz-avatar-spacer,
:host(.ws-tenant-thewineengine) .ws-quiz-avatar-spacer {
  visibility: hidden;
}

:host(.ws-tenant-wineengine) .ws-quiz-option-container,
:host(.ws-tenant-thewineengine) .ws-quiz-option-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  max-width: 90%;
}

:host(.ws-tenant-wineengine) .ws-quiz-question,
:host(.ws-tenant-thewineengine) .ws-quiz-question {
  background: var(--ws-surface, #f5f5f5);
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 15px;
  line-height: 1.4;
  color: var(--ws-text, #1d1d1d);
  max-width: 100%;
}

:host(.ws-tenant-wineengine) .ws-quiz-options,
:host(.ws-tenant-thewineengine) .ws-quiz-options {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
}

:host(.ws-tenant-wineengine) .ws-quiz-option-btn,
:host(.ws-tenant-thewineengine) .ws-quiz-option-btn {
  /* background: #ffffff;
  border: 1px solid #574d39;
  border-radius: 8px;
  color: #1d1d1d;
  padding: 8px 12px;
  font-size: 15px;
  font-weight: 600;
  line-height: 16px;
  cursor: pointer;
  text-align: left;
  font-family: var(--ws-font);
  min-height: 35px;
  width: 100%;
  max-width: 100%;
  box-shadow: none; */
  background-color: #ffffff;
  border: 1px solid #574d39;
  border-radius: 8px;
  color: #1d1d1d;
  -webkit-tap-highlight-color: transparent;
  padding: 8px 12px 8px 12px;
  font-size: 15px !important;
  font-weight: 600 !important;
  line-height: 16px;
  cursor: pointer;
  text-align: left !important;
  font-family: "Open Sans", serif !important;
  margin-right: 5px;
  min-height: 35px;
  display: flex;
  gap: 5px;
  -webkit-text-size-adjust: 100%;
  box-shadow: none !important;
}

:host(.ws-tenant-wineengine) .ws-quiz-option-btn.is-selected,
:host(.ws-tenant-thewineengine) .ws-quiz-option-btn.is-selected {
  background: var(--ws-selected-chip, var(--ws-primary));
  border-color: var(--ws-selected-chip, var(--ws-primary));
  color: #fff;
}

:host(.ws-tenant-wineengine)
  .ws-quiz-option-btn:hover:not(:disabled):not(.is-selected),
:host(.ws-tenant-thewineengine)
  .ws-quiz-option-btn:hover:not(:disabled):not(.is-selected) {
  background: var(--ws-selected-chip, var(--ws-primary));
  border-color: var(--ws-selected-chip, var(--ws-primary));
  color: #ffffff;
}

:host(.ws-tenant-wineengine) .ws-quiz-option-btn:disabled,
:host(.ws-tenant-thewineengine) .ws-quiz-option-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

:host(.ws-tenant-wineengine) .ws-quiz-ended-button,
:host(.ws-tenant-thewineengine) .ws-quiz-ended-button {
  padding: 8px 12px;
  border: 1px solid rgba(142, 142, 142, 0.5);
  border-radius: 8px;
  color: #1d1d1d;
  background-color: #fff;
  font-weight: 600;
  font-size: 15px;
}

:host(.ws-tenant-wineengine) .ws-quiz-form-container,
:host(.ws-tenant-thewineengine) .ws-quiz-form-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px; /* old .quiz-form-container — space between fields and Submit */
  width: 100%;
  margin-top: 8px;
  padding: 16px 24px;
  box-sizing: border-box;
  border-radius: 12px;
  border: 1px solid #f1f1f1;
  background: #fff;
  box-shadow: 0 0 10px 0 rgba(156, 85, 100, 0.1);
}

:host(.ws-tenant-wineengine) .ws-quiz-form-title,
:host(.ws-tenant-thewineengine) .ws-quiz-form-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 8px;
}

:host(.ws-tenant-wineengine) .ws-quiz-form-description,
:host(.ws-tenant-thewineengine) .ws-quiz-form-description {
  font-size: 14px;
  color: #8e8e8e;
  font-weight: 600;
  margin-bottom: 0;
}

:host(.ws-tenant-wineengine) .ws-quiz-fields,
:host(.ws-tenant-thewineengine) .ws-quiz-fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

:host(.ws-tenant-wineengine) .ws-quiz-form-footer,
:host(.ws-tenant-thewineengine) .ws-quiz-form-footer {
  width: 100%;
}

:host(.ws-tenant-wineengine) .ws-quiz-field-group label,
:host(.ws-tenant-thewineengine) .ws-quiz-field-group label {
  display: block;
  padding-bottom: 7px;
  font-size: 14px;
  font-weight: 600;
}

:host(.ws-tenant-wineengine) .ws-quiz-field-group input,
:host(.ws-tenant-thewineengine) .ws-quiz-field-group input {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 15px;
  font-family: var(--ws-font);
}

:host(.ws-tenant-wineengine) .ws-quiz-error-username,
:host(.ws-tenant-wineengine) .ws-quiz-error-email,
:host(.ws-tenant-thewineengine) .ws-quiz-error-username,
:host(.ws-tenant-thewineengine) .ws-quiz-error-email {
  color: red;
  font-size: 12px;
  padding-top: 2px;
  display: block;
}

:host(.ws-tenant-wineengine) .ws-quiz-form-submit,
:host(.ws-tenant-thewineengine) .ws-quiz-form-submit {
  padding: 10px 12px;
  width: 100%;
  text-align: center;
  background-color: #1d1d1d;
  color: #fff;
  font-size: 15px;
  cursor: pointer;
  border: none;
  border-radius: 10px;
  font-family: var(--ws-font);
}

:host(.ws-tenant-wineengine) .ws-quiz-form-submit.submitted,
:host(.ws-tenant-thewineengine) .ws-quiz-form-submit.submitted {
  background: var(--ws-primary);
}

:host(.ws-tenant-wineengine) .ws-quiz-skip,
:host(.ws-tenant-thewineengine) .ws-quiz-skip {
  margin-top: 16px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: underline;
  color: #8e8e8e;
  cursor: pointer;
  text-align: center;
}

:host(.ws-tenant-wineengine) .ws-card-quiz-result,
:host(.ws-tenant-thewineengine) .ws-card-quiz-result {
  background-color: #fff;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 0 10px 0 rgba(156, 85, 100, 0.1);
  width: 320px;
  max-width: 100%;
  padding: 8px;
}

:host(.ws-tenant-wineengine) .ws-card-quiz-wine-image img,
:host(.ws-tenant-thewineengine) .ws-card-quiz-wine-image img {
  object-fit: contain;
  border-radius: 6px;
  width: 100%;
  min-height: 171px;
}

:host(.ws-tenant-wineengine) .ws-card-quiz-result-title,
:host(.ws-tenant-thewineengine) .ws-card-quiz-result-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 8px;
}

:host(.ws-tenant-wineengine) .ws-card-quiz-result-desc,
:host(.ws-tenant-thewineengine) .ws-card-quiz-result-desc {
  color: #8e8e8e;
  font-size: 14px;
  font-weight: 600;
}

:host(.ws-tenant-wineengine) .ws-quiz-end-container,
:host(.ws-tenant-thewineengine) .ws-quiz-end-container {
  position: relative;
}

:host(.ws-tenant-wineengine) #ws-quiz-end-toggle,
:host(.ws-tenant-thewineengine) #ws-quiz-end-toggle {
  height: 32px;
  padding: 8px 12px;
  border: none;
  border-radius: 16px;
  background: #f7f7f7;
  position: absolute;
  z-index: 100;
  left: 50%;
  bottom: 72px;
  transform: translate(-50%, -50%);
  color: #1d1d1d;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

:host(.ws-tenant-wineengine) #ws-quiz-end-overlay,
:host(.ws-tenant-thewineengine) #ws-quiz-end-overlay {
  position: absolute;
  inset: 0;
  z-index: 102;
  background-color: rgba(0, 0, 0, 0.51);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

:host(.ws-tenant-wineengine) #ws-quiz-end-overlay[hidden],
:host(.ws-tenant-thewineengine) #ws-quiz-end-overlay[hidden],
:host(.ws-tenant-wineengine) .ws-quiz-end-container[hidden],
:host(.ws-tenant-thewineengine) .ws-quiz-end-container[hidden] {
  display: none !important;
}

:host(.ws-tenant-wineengine) .ws-quiz-end-modal,
:host(.ws-tenant-thewineengine) .ws-quiz-end-modal {
  border-radius: 12px;
  border: 1px solid #f1f1f1;
  padding: 16px 24px;
  width: 80%;
  max-width: 320px;
  background-color: #fff;
  box-shadow: 0 0 10px 0 rgba(156, 85, 100, 0.1);
}

:host(.ws-tenant-wineengine) .ws-quiz-end-modal-title,
:host(.ws-tenant-thewineengine) .ws-quiz-end-modal-title {
  font-size: 20px;
  font-weight: 600;
  color: #1d1d1d;
  margin-bottom: 8px;
  display: block;
}

:host(.ws-tenant-wineengine) .ws-quiz-end-modal-description,
:host(.ws-tenant-thewineengine) .ws-quiz-end-modal-description {
  font-size: 15px;
  font-weight: 600;
  color: #8e8e8e;
  margin-bottom: 24px;
}

:host(.ws-tenant-wineengine) .ws-quiz-end-action,
:host(.ws-tenant-thewineengine) .ws-quiz-end-action {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

:host(.ws-tenant-wineengine) .ws-quiz-end-action button,
:host(.ws-tenant-thewineengine) .ws-quiz-end-action button {
  padding: 8px 10px;
  font-size: 15px;
  border: 1px solid #574d39;
  font-weight: 400;
  cursor: pointer;
  flex: 1;
}

:host(.ws-tenant-wineengine) .ws-quiz-keep-going,
:host(.ws-tenant-thewineengine) .ws-quiz-keep-going {
  background-color: #fff;
}

:host(.ws-tenant-wineengine) .ws-quiz-end-confirm,
:host(.ws-tenant-thewineengine) .ws-quiz-end-confirm {
  background: #1d1d1d;
  color: #fff;
}

/* ── Build Wine Box (old _wine_box.scss parity) ───────────────────────── */
/* Drawer sits in flex flow (before-input); do not add messages padding-bottom
   or it becomes empty scrollable space below the wine listing. */

:host(.ws-tenant-wineengine) .wine-box-toast-host,
:host(.ws-tenant-thewineengine) .wine-box-toast-host {
  position: absolute;
  top: 68px;
  left: 10px;
  right: 10px;
  z-index: 10060;
  pointer-events: none;
}
:host(.ws-tenant-wineengine) .wine-box-toast-host:empty,
:host(.ws-tenant-thewineengine) .wine-box-toast-host:empty {
  display: none;
}

:host(.ws-tenant-wineengine) .wine-box-toast,
:host(.ws-tenant-thewineengine) .wine-box-toast {
  pointer-events: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  font-family: var(--ws-font);
}
:host(.ws-tenant-wineengine) .wine-box-toast-icon,
:host(.ws-tenant-thewineengine) .wine-box-toast-icon {
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: grid;
  place-items: center;
}
:host(.ws-tenant-wineengine) .wine-box-toast-icon svg,
:host(.ws-tenant-thewineengine) .wine-box-toast-icon svg {
  display: block;
}
:host(.ws-tenant-wineengine) .wine-box-toast-icon--upgrade,
:host(.ws-tenant-thewineengine) .wine-box-toast-icon--upgrade {
  background: #e8f5e9;
  color: #2e7d32;
}
:host(.ws-tenant-wineengine) .wine-box-toast-icon--downgrade,
:host(.ws-tenant-thewineengine) .wine-box-toast-icon--downgrade {
  background: #fff8e1;
  color: #f9a825;
}
:host(.ws-tenant-wineengine) .wine-box-toast-body,
:host(.ws-tenant-thewineengine) .wine-box-toast-body {
  flex: 1;
  min-width: 0;
  text-align: left;
}
:host(.ws-tenant-wineengine) .wine-box-toast-body strong,
:host(.ws-tenant-thewineengine) .wine-box-toast-body strong {
  display: block;
  color: #000;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 1px;
}
:host(.ws-tenant-wineengine) .wine-box-toast-body p,
:host(.ws-tenant-thewineengine) .wine-box-toast-body p {
  margin: 0;
  color: #000;
  font-size: 11px;
  line-height: 1.3;
  font-weight: 400;
}
:host(.ws-tenant-wineengine) .wine-box-toast-close,
:host(.ws-tenant-thewineengine) .wine-box-toast-close {
  flex: 0 0 auto;
  border: none;
  background: transparent;
  color: #999;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  font-size: 16px;
}

:host(.ws-tenant-wineengine) .ws-wine-box-drawer-root,
:host(.ws-tenant-thewineengine) .ws-wine-box-drawer-root {
  flex-shrink: 0;
  width: 100%;
}

:host(.ws-tenant-wineengine) .wine-box-drawer,
:host(.ws-tenant-thewineengine) .wine-box-drawer {
  display: none;
  flex-direction: column;
  flex-shrink: 0;
  background: #fff;
  border-top: 1px solid rgba(156, 85, 100, 0.18);
  box-shadow: 0 -2px 12px rgba(87, 77, 57, 0.1);
  font-family: var(--ws-font);
  z-index: 10040;
}
:host(.ws-tenant-wineengine) .wine-box-drawer.is-visible,
:host(.ws-tenant-thewineengine) .wine-box-drawer.is-visible {
  display: flex;
}

:host(.ws-tenant-wineengine) .wine-box-tier-row,
:host(.ws-tenant-thewineengine) .wine-box-tier-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px;
  background: #faf7f8;
  border-bottom: 1px solid rgba(156, 85, 100, 0.1);
}
:host(.ws-tenant-wineengine) .wine-box-subscribe-banner,
:host(.ws-tenant-thewineengine) .wine-box-subscribe-banner {
  display: none;
  align-items: center;
  flex: 1;
  gap: 6px;
  min-width: 0;
}
:host(.ws-tenant-wineengine) .wine-box-banner-close,
:host(.ws-tenant-thewineengine) .wine-box-banner-close {
  flex: 0 0 auto;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0 2px;
}
:host(.ws-tenant-wineengine) .wine-box-banner-text,
:host(.ws-tenant-thewineengine) .wine-box-banner-text {
  flex: 1;
  margin: 0;
  color: #000;
  font-size: 14px;
  font-weight: 300;
  line-height: 1.2;
}
:host(.ws-tenant-wineengine) .wine-box-banner-text strong,
:host(.ws-tenant-thewineengine) .wine-box-banner-text strong {
  font-weight: 700;
}

:host(.ws-tenant-wineengine) .wine-box-drawer.is-oneoff .wine-box-tier-row,
:host(.ws-tenant-thewineengine) .wine-box-drawer.is-oneoff .wine-box-tier-row {
  background: linear-gradient(270deg, #fdf8f9 0%, #7a2840 140.26%);
  padding: 3px 8px;
  border-bottom: none;
}
:host(.ws-tenant-wineengine) .wine-box-drawer.is-oneoff .wine-box-tiers,
:host(.ws-tenant-thewineengine) .wine-box-drawer.is-oneoff .wine-box-tiers {
  display: none;
}
:host(.ws-tenant-wineengine)
  .wine-box-drawer.is-oneoff
  .wine-box-subscribe-banner,
:host(.ws-tenant-thewineengine)
  .wine-box-drawer.is-oneoff
  .wine-box-subscribe-banner {
  display: flex;
}

:host(.ws-tenant-wineengine) .wine-box-tiers,
:host(.ws-tenant-thewineengine) .wine-box-tiers {
  display: flex;
  flex: 1;
  min-width: 0;
  gap: 3px;
}
:host(.ws-tenant-wineengine) .wine-box-tier-btn,
:host(.ws-tenant-thewineengine) .wine-box-tier-btn {
  flex: 1;
  min-width: 0;
  border: none;
  border-radius: 5px;
  padding: 3px 2px;
  min-height: 24px;
  background: #f3e8eb;
  color: var(--ws-rich-cta-bg, #7a2840);
  cursor: pointer;
  font-family: var(--ws-font);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.15;
  text-align: center;
  white-space: normal;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
:host(.ws-tenant-wineengine) .wine-box-tier-btn .wine-box-tier-save,
:host(.ws-tenant-thewineengine) .wine-box-tier-btn .wine-box-tier-save {
  display: block;
  font-size: 12px;
  font-weight: 600;
  opacity: 0.92;
  margin-top: 1px;
}
:host(.ws-tenant-wineengine) .wine-box-tier-btn.is-active,
:host(.ws-tenant-thewineengine) .wine-box-tier-btn.is-active {
  background: var(--ws-rich-cta-bg, #7a2840);
  color: #fff;
}

:host(.ws-tenant-wineengine) .wine-box-frequency,
:host(.ws-tenant-thewineengine) .wine-box-frequency {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  align-self: center;
  border-radius: 999px;
  border: 1px solid #7a2840;
  background: #f3e8eb;
  min-width: 96px;
  height: 22px;
  gap: 0;
}
:host(.ws-tenant-wineengine) .wine-box-frequency-btn,
:host(.ws-tenant-thewineengine) .wine-box-frequency-btn {
  flex: 1;
  height: 100%;
  min-width: 0;
  border: 1px solid transparent;
  border-radius: 999px;
  padding: 0 7px;
  background: transparent;
  color: #7a2840;
  cursor: pointer;
  font-family: var(--ws-font);
  font-size: 9px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
:host(.ws-tenant-wineengine) .wine-box-frequency-btn.is-active,
:host(.ws-tenant-thewineengine) .wine-box-frequency-btn.is-active {
  background: #7a2840;
  color: #fff;
  border-color: #7a2840;
}

:host(.ws-tenant-wineengine) .wine-box-selection,
:host(.ws-tenant-thewineengine) .wine-box-selection {
  padding: 2px 8px 1px;
}
:host(.ws-tenant-wineengine) .wine-box-selection-header,
:host(.ws-tenant-thewineengine) .wine-box-selection-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1px;
  color: #1d1d1d;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.1;
}
:host(.ws-tenant-wineengine) .wine-box-expand-btn,
:host(.ws-tenant-thewineengine) .wine-box-expand-btn {
  border: none;
  background: transparent;
  color: var(--ws-rich-cta-bg, #7a2840);
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
}
:host(.ws-tenant-wineengine) .wine-box-slots,
:host(.ws-tenant-thewineengine) .wine-box-slots {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 2px;
  scrollbar-width: none;
}
:host(.ws-tenant-wineengine) .wine-box-slots::-webkit-scrollbar,
:host(.ws-tenant-thewineengine) .wine-box-slots::-webkit-scrollbar {
  display: none;
}
:host(.ws-tenant-wineengine) .wine-box-slot,
:host(.ws-tenant-thewineengine) .wine-box-slot {
  position: relative;
  flex: 0 0 68px;
  width: 68px;
  height: 32px;
  border: 1px dashed rgba(156, 85, 100, 0.4);
  border-radius: 8px;
  background: #fff;
  padding: 8px 4px 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  box-sizing: border-box;
}
:host(.ws-tenant-wineengine) .wine-box-slot.is-filled,
:host(.ws-tenant-thewineengine) .wine-box-slot.is-filled {
  background: #fdf9fa;
  border-style: dashed;
  border-color: rgba(156, 85, 100, 0.38);
}
:host(.ws-tenant-wineengine) .wine-box-slot.is-empty,
:host(.ws-tenant-thewineengine) .wine-box-slot.is-empty {
  color: rgba(156, 85, 100, 0.42);
  font-size: 10px;
  font-weight: 600;
  padding-top: 0;
}
:host(.ws-tenant-wineengine) .wine-box-slot-label,
:host(.ws-tenant-thewineengine) .wine-box-slot-label {
  font-size: 10px;
  font-weight: 700;
  line-height: 1.15;
  color: #1d1d1d;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
  width: 100%;
}
:host(.ws-tenant-wineengine) .wine-box-slot-remove,
:host(.ws-tenant-thewineengine) .wine-box-slot-remove {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 14px;
  height: 14px;
  border: none;
  border-radius: 50%;
  background: #fff;
  color: #555;
  cursor: pointer;
  padding: 0;
  display: grid;
  place-items: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.14);
  line-height: 1;
  font-size: 10px;
}

:host(.ws-tenant-wineengine) .wine-box-footer,
:host(.ws-tenant-thewineengine) .wine-box-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 2px 8px;
  border-top: 1px solid rgba(156, 85, 100, 0.1);
  background: #fff;
}
:host(.ws-tenant-wineengine) .wine-box-footer-main,
:host(.ws-tenant-thewineengine) .wine-box-footer-main {
  flex: 1;
  min-width: 0;
}
:host(.ws-tenant-wineengine) .wine-box-subtotal-row,
:host(.ws-tenant-thewineengine) .wine-box-subtotal-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 2px;
}
:host(.ws-tenant-wineengine) .wine-box-subtotal-label,
:host(.ws-tenant-wineengine) .wine-box-subtotal-label,
:host(.ws-tenant-wineengine) .wine-box-subtotal-now,
:host(.ws-tenant-thewineengine) .wine-box-subtotal-now {
  color: #1d1d1d;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.1;
}
:host(.ws-tenant-wineengine) .wine-box-shipping-msg,
:host(.ws-tenant-thewineengine) .wine-box-shipping-msg {
  margin: 0;
  font-size: 10px;
  line-height: 1.3;
  color: #1d1d1d;
  font-weight: 400;
}
:host(.ws-tenant-wineengine) .wine-box-shipping-amount,
:host(.ws-tenant-thewineengine) .wine-box-shipping-amount {
  color: var(--ws-rich-cta-bg, #7a2840);
  font-weight: 700;
}
:host(.ws-tenant-wineengine) .wine-box-shipping-unlocked,
:host(.ws-tenant-thewineengine) .wine-box-shipping-unlocked {
  color: #2e7d32;
  font-weight: 700;
}
:host(.ws-tenant-wineengine) .wine-box-continue-btn,
:host(.ws-tenant-thewineengine) .wine-box-continue-btn {
  flex: 0 0 auto;
  align-self: center;
  min-width: 100px;
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  background: var(--ws-rich-cta-bg, #7a2840);
  color: #fff;
  cursor: pointer;
  font-family: var(--ws-font);
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
}
:host(.ws-tenant-wineengine) .wine-box-continue-btn:disabled,
:host(.ws-tenant-thewineengine) .wine-box-continue-btn:disabled {
  background: #b88494;
  cursor: not-allowed;
}

/* Product qty helpers — card-specific styles live above under .ws-we-card */

/* Build Now CTA on membership cards */
:host(.ws-tenant-wineengine) .ws-we-build-now,
:host(.ws-tenant-thewineengine) .ws-we-build-now {
  border: none;
  cursor: pointer;
  text-decoration: none;
}

/* Your Cart panel */
:host(.ws-tenant-wineengine) .build-box-cart-message,
:host(.ws-tenant-thewineengine) .build-box-cart-message {
  width: 100%;
}
:host(.ws-tenant-wineengine) .build-box-cart-container,
:host(.ws-tenant-thewineengine) .build-box-cart-container {
  font-family: var(--ws-font);
  color: #1d1d1d;
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  max-width: 100%;
  background: transparent;
  border-radius: 0;
  box-shadow: none;
  padding: 0 8px;
  margin-left: 0;
}
:host(.ws-tenant-wineengine) .build-box-cart-header,
:host(.ws-tenant-thewineengine) .build-box-cart-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 38px;
  margin: 0 0 8px;
  padding: 0 10px 0 14px;
  border-top: 1px solid rgba(156, 85, 100, 0.22);
  border-bottom: 1px solid rgba(156, 85, 100, 0.18);
  background: rgba(255, 248, 250, 0.78);
}
:host(.ws-tenant-wineengine) .build-box-cart-title,
:host(.ws-tenant-thewineengine) .build-box-cart-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.1;
  font-family: var(--ws-font);
}
:host(.ws-tenant-wineengine) .build-box-cart-container .wine-box-frequency,
:host(.ws-tenant-thewineengine) .build-box-cart-container .wine-box-frequency {
  flex: 0 0 auto;
  height: 25px;
  min-width: 108px;
  border: 1px solid var(--ws-rich-cta-bg, #7a2840);
  border-radius: 999px;
  background: #fff;
  overflow: hidden;
}
:host(.ws-tenant-wineengine) .build-box-cart-container .wine-box-frequency-btn,
:host(.ws-tenant-thewineengine)
  .build-box-cart-container
  .wine-box-frequency-btn {
  height: 100%;
  padding: 0 10px;
  border: 0;
  border-radius: 0;
  background: #fff;
  color: var(--ws-rich-cta-bg, #7a2840);
  font-size: 10px;
  font-weight: 500;
}
:host(.ws-tenant-wineengine)
  .build-box-cart-container
  .wine-box-frequency-btn.is-active,
:host(.ws-tenant-thewineengine)
  .build-box-cart-container
  .wine-box-frequency-btn.is-active {
  background: var(--ws-rich-cta-bg, #7a2840);
  color: #fff;
}
:host(.ws-tenant-wineengine) .build-box-cart-items-scroll,
:host(.ws-tenant-thewineengine) .build-box-cart-items-scroll {
  max-height: 296px;
  overflow-y: auto;
  overflow-x: hidden;
  margin: 0;
  padding: 0 10px;
  scrollbar-width: thin;
}
:host(.ws-tenant-wineengine)
  .build-box-cart-container.is-monthly
  .build-box-cart-items-scroll,
:host(.ws-tenant-thewineengine)
  .build-box-cart-container.is-monthly
  .build-box-cart-items-scroll {
  max-height: 270px;
}
:host(.ws-tenant-wineengine)
  .build-box-cart-container.is-oneoff
  .build-box-cart-items-scroll,
:host(.ws-tenant-thewineengine)
  .build-box-cart-container.is-oneoff
  .build-box-cart-items-scroll {
  max-height: 444px;
  padding-bottom: 8px;
}
:host(.ws-tenant-wineengine) .build-box-cart-item,
:host(.ws-tenant-thewineengine) .build-box-cart-item {
  position: relative;
  display: grid;
  grid-template-columns: 50px minmax(0, 1fr);
  gap: 12px;
  align-items: center;
  padding: 9px 0;
  border: 0;
  border-bottom: 1px solid rgba(229, 229, 229, 0.75);
  border-radius: 0;
  background: transparent;
}
:host(.ws-tenant-wineengine) .build-box-cart-item-image,
:host(.ws-tenant-thewineengine) .build-box-cart-item-image {
  display: flex;
  align-items: center;
  justify-content: center;
}
:host(.ws-tenant-wineengine) .build-box-cart-item-image img,
:host(.ws-tenant-thewineengine) .build-box-cart-item-image img {
  width: 36px;
  height: auto;
  max-height: 76px;
  object-fit: contain;
}
:host(.ws-tenant-wineengine) .build-box-cart-item-body,
:host(.ws-tenant-thewineengine) .build-box-cart-item-body {
  position: relative;
  min-width: 0;
  padding-right: 126px;
}
:host(.ws-tenant-wineengine) .build-box-cart-item-title,
:host(.ws-tenant-thewineengine) .build-box-cart-item-title {
  margin: 0 0 3px;
  padding: 0;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.18;
  color: #1d1d1d;
  overflow-wrap: anywhere;
}
:host(.ws-tenant-wineengine) .build-box-cart-item-price,
:host(.ws-tenant-thewineengine) .build-box-cart-item-price {
  margin: 0;
  padding: 0;
  font-size: 12px;
  line-height: 1.1;
}
:host(.ws-tenant-wineengine) .build-box-cart-item-price strong,
:host(.ws-tenant-thewineengine) .build-box-cart-item-price strong {
  font-weight: 700;
  color: #1d1d1d;
}
:host(.ws-tenant-wineengine) .build-box-cart-item-actions,
:host(.ws-tenant-thewineengine) .build-box-cart-item-actions {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  width: 120px;
}
:host(.ws-tenant-wineengine) .build-box-cart-item-actions .product-action,
:host(.ws-tenant-thewineengine) .build-box-cart-item-actions .product-action {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
  margin-left: 0;
  width: auto;
}
:host(.ws-tenant-wineengine)
  .build-box-cart-item-actions
  .product-quantity-wrapper,
:host(.ws-tenant-thewineengine)
  .build-box-cart-item-actions
  .product-quantity-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 0;
}
:host(.ws-tenant-wineengine) .build-box-cart-item-actions button,
:host(.ws-tenant-wineengine) .build-box-cart-item-actions .cart-qty,
:host(.ws-tenant-wineengine) .build-box-cart-item-actions .inc-qty,
:host(.ws-tenant-wineengine) .build-box-cart-item-actions .dec-qty,
:host(.ws-tenant-wineengine) .build-box-cart-item-actions .delete-cart,
:host(.ws-tenant-thewineengine) .build-box-cart-item-actions button,
:host(.ws-tenant-thewineengine) .build-box-cart-item-actions .cart-qty,
:host(.ws-tenant-thewineengine) .build-box-cart-item-actions .inc-qty,
:host(.ws-tenant-thewineengine) .build-box-cart-item-actions .dec-qty,
:host(.ws-tenant-thewineengine) .build-box-cart-item-actions .delete-cart {
  flex: 0 0 28px;
  width: 28px;
  min-width: 28px;
  max-width: 28px;
  height: 28px;
  min-height: 28px;
  padding: 0 !important;
  border: 1px solid #e5e5e5;
  border-radius: 2px;
  background: #fff;
  color: #111;
  box-shadow: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
}
:host(.ws-tenant-wineengine) .build-box-cart-item-actions .cart-qty,
:host(.ws-tenant-thewineengine) .build-box-cart-item-actions .cart-qty {
  text-align: center;
  font-size: 12px;
  cursor: default;
}
:host(.ws-tenant-wineengine) .build-box-cart-item-actions .delete-cart svg,
:host(.ws-tenant-thewineengine) .build-box-cart-item-actions .delete-cart svg {
  width: 15px;
  height: 15px;
}

:host(.ws-tenant-wineengine) .build-box-size-section,
:host(.ws-tenant-thewineengine) .build-box-size-section {
  margin: 8px 10px 10px;
}
:host(.ws-tenant-wineengine) .build-box-size-header,
:host(.ws-tenant-thewineengine) .build-box-size-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
  font-size: 11px;
  font-weight: 700;
  color: var(--ws-rich-cta-bg, #7a2840);
  letter-spacing: 0.04em;
}
:host(.ws-tenant-wineengine) .build-box-edit-size,
:host(.ws-tenant-thewineengine) .build-box-edit-size {
  border: none;
  background: transparent;
  color: #1d1d1d;
  font-size: 11px;
  font-weight: 400;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
}
:host(.ws-tenant-wineengine) .build-box-size-card,
:host(.ws-tenant-thewineengine) .build-box-size-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 34px;
  padding: 0 10px;
  border: 1px solid rgba(156, 85, 100, 0.25);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.78);
  font-size: 12px;
  font-weight: 600;
}
:host(.ws-tenant-wineengine) .build-box-size-discount,
:host(.ws-tenant-thewineengine) .build-box-size-discount {
  color: #2e7d32;
  font-weight: 700;
}

:host(.ws-tenant-wineengine) .build-box-coupon-section,
:host(.ws-tenant-thewineengine) .build-box-coupon-section {
  display: flex;
  gap: 9px;
  margin: 0 10px 10px;
}
:host(.ws-tenant-wineengine) .build-box-coupon-input,
:host(.ws-tenant-thewineengine) .build-box-coupon-input {
  flex: 1;
  min-width: 0;
  height: 38px;
  padding: 0 12px;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  color: #1d1d1d;
  font-size: 15px;
  font-family: var(--ws-font);
}
:host(.ws-tenant-wineengine) .build-box-coupon-apply,
:host(.ws-tenant-thewineengine) .build-box-coupon-apply {
  flex: 0 0 auto;
  border: none;
  border-radius: 2px;
  min-width: 102px;
  padding: 0 18px;
  background: var(--ws-rich-cta-bg, #7a2840);
  color: #fff;
  font-size: 16px;
  font-weight: 400;
  cursor: pointer;
  font-family: var(--ws-font);
}

:host(.ws-tenant-wineengine) .build-box-journey,
:host(.ws-tenant-thewineengine) .build-box-journey {
  margin: 12px 0 0;
  padding: 12px 10px 10px;
  background: rgba(255, 255, 255, 0.74);
  box-shadow: 0 -8px 18px rgba(255, 255, 255, 0.86);
}
:host(.ws-tenant-wineengine) .build-box-journey-header,
:host(.ws-tenant-thewineengine) .build-box-journey-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 7px;
  font-size: 9px;
  font-weight: 700;
}
:host(.ws-tenant-wineengine) .build-box-journey-total,
:host(.ws-tenant-thewineengine) .build-box-journey-total {
  color: var(--ws-rich-cta-bg, #7a2840);
  font-weight: 600;
}
:host(.ws-tenant-wineengine) .build-box-journey-track-wrap,
:host(.ws-tenant-thewineengine) .build-box-journey-track-wrap {
  position: relative;
  padding-bottom: 24px;
}
:host(.ws-tenant-wineengine) .build-box-journey-track,
:host(.ws-tenant-thewineengine) .build-box-journey-track {
  height: 7px;
  background: #e5e5e5;
  border-radius: 999px;
  overflow: hidden;
}
:host(.ws-tenant-wineengine) .build-box-journey-fill,
:host(.ws-tenant-thewineengine) .build-box-journey-fill {
  height: 100%;
  background: #3cbc5d;
  border-radius: 999px;
  transition: width 0.3s ease;
}
:host(.ws-tenant-wineengine) .build-box-journey-markers,
:host(.ws-tenant-thewineengine) .build-box-journey-markers {
  display: flex;
  justify-content: space-between;
  position: absolute;
  left: 0;
  right: 0;
  top: -4px;
}
:host(.ws-tenant-wineengine) .build-box-journey-marker,
:host(.ws-tenant-thewineengine) .build-box-journey-marker {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
}
:host(.ws-tenant-wineengine) .build-box-journey-dot,
:host(.ws-tenant-thewineengine) .build-box-journey-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #e5e5e5;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px #e5e5e5;
}
:host(.ws-tenant-wineengine)
  .build-box-journey-marker.is-active
  .build-box-journey-dot,
:host(.ws-tenant-thewineengine)
  .build-box-journey-marker.is-active
  .build-box-journey-dot {
  background: #3cbc5d;
  box-shadow: 0 0 0 1px #3cbc5d;
}
:host(.ws-tenant-wineengine)
  .build-box-journey-marker.is-current
  .build-box-journey-dot,
:host(.ws-tenant-thewineengine)
  .build-box-journey-marker.is-current
  .build-box-journey-dot {
  width: 18px;
  height: 18px;
  margin-top: -4px;
}
:host(.ws-tenant-wineengine) .build-box-journey-label,
:host(.ws-tenant-thewineengine) .build-box-journey-label {
  font-size: 8px;
  font-weight: 600;
  color: #999;
  text-align: center;
  line-height: 1.1;
}
:host(.ws-tenant-wineengine)
  .build-box-journey-marker.is-active
  .build-box-journey-label,
:host(.ws-tenant-thewineengine)
  .build-box-journey-marker.is-active
  .build-box-journey-label {
  color: #3cbc5d;
}

:host(.ws-tenant-wineengine) .build-box-cart-footer,
:host(.ws-tenant-thewineengine) .build-box-cart-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: 0;
  padding: 9px 10px 8px;
  border-top: 1px solid rgba(229, 229, 229, 0.8);
  background: rgba(255, 255, 255, 0.94);
}
:host(.ws-tenant-wineengine) .build-box-cart-footer-main,
:host(.ws-tenant-thewineengine) .build-box-cart-footer-main {
  flex: 1;
  min-width: 0;
}
:host(.ws-tenant-wineengine) .build-box-cart-subtotal-row,
:host(.ws-tenant-thewineengine) .build-box-cart-subtotal-row {
  display: flex;
  align-items: baseline;
  gap: 7px;
  margin-bottom: 2px;
}
:host(.ws-tenant-wineengine) .build-box-cart-subtotal-label,
:host(.ws-tenant-thewineengine) .build-box-cart-subtotal-label {
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
}
:host(.ws-tenant-wineengine) .build-box-cart-subtotal-prices,
:host(.ws-tenant-thewineengine) .build-box-cart-subtotal-prices {
  display: flex;
  align-items: baseline;
  gap: 6px;
}
:host(.ws-tenant-wineengine) .build-box-cart-subtotal-was,
:host(.ws-tenant-thewineengine) .build-box-cart-subtotal-was {
  color: #9b9b9b;
  font-size: 14px;
}
:host(.ws-tenant-wineengine) .build-box-cart-subtotal-now,
:host(.ws-tenant-thewineengine) .build-box-cart-subtotal-now {
  font-size: 16px;
  font-weight: 700;
  color: #1d1d1d;
}
:host(.ws-tenant-wineengine) .build-box-cart-shipping,
:host(.ws-tenant-thewineengine) .build-box-cart-shipping {
  margin: 0;
  font-size: 10px;
  line-height: 1.3;
}
:host(.ws-tenant-wineengine) .build-box-checkout-btn,
:host(.ws-tenant-thewineengine) .build-box-checkout-btn {
  flex: 0 0 auto;
  border: none;
  border-radius: 2px;
  min-width: 110px;
  height: 28px;
  padding: 0 14px;
  background: var(--ws-rich-cta-bg, #7a2840);
  color: #fff;
  font-size: 0;
  font-weight: 400;
  cursor: pointer;
  font-family: var(--ws-font);
  white-space: nowrap;
}
:host(.ws-tenant-wineengine) .build-box-checkout-btn::after,
:host(.ws-tenant-thewineengine) .build-box-checkout-btn::after {
  content: "Proceed to checkout";
  font-size: 14px;
  line-height: 28px;
}
:host(.ws-tenant-wineengine) .build-box-checkout-btn:disabled,
:host(.ws-tenant-thewineengine) .build-box-checkout-btn:disabled {
  background: #b88494 !important;
  cursor: not-allowed !important;
}

@media (max-width: 768px) {
  :host(.ws-tenant-wineengine) .wine-box-toast-host,
  :host(.ws-tenant-thewineengine) .wine-box-toast-host {
    top: 60px;
  }
  :host(.ws-tenant-wineengine) .wine-box-tier-btn,
  :host(.ws-tenant-thewineengine) .wine-box-tier-btn {
    font-size: 8px;
    min-height: 22px;
    padding: 3px 1px;
  }
  :host(.ws-tenant-wineengine) .build-box-cart-footer,
  :host(.ws-tenant-thewineengine) .build-box-cart-footer {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    padding: 12px 10px;
  }
  :host(.ws-tenant-wineengine) .build-box-checkout-btn,
  :host(.ws-tenant-thewineengine) .build-box-checkout-btn {
    width: 100%;
    min-width: 0;
    height: 34px;
  }
  :host(.ws-tenant-wineengine) .build-box-checkout-btn::after,
  :host(.ws-tenant-thewineengine) .build-box-checkout-btn::after {
    line-height: 34px;
    font-size: 13px;
  }
}
